import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { Card, Grid, IconButton } from "@mui/material";
import { Text } from "components/atoms/text";
import { Chart } from "components/molecules/chart";
import { useActAs } from "contexts/ActAsContext";
import { DomainsTable } from "containers/domains-table";
import PracticeListTable from "containers/practice-list-table/practice-list-table";
import ResearchListTable from "containers/search-list-table/search-list-table";
import { DASHBOARD_PERIOD_FOR_ALL } from "core/constants/feature-flags";
import { isOperatorRole } from "core/constants/roles";
import { authSelectors } from "core/store/modules/auth";
import { practiceAsync, practiceSelectors } from "core/store/modules/practice";
import { reportSelectors } from "core/store/modules/report";
import { searchAsync, searchSelectors } from "core/store/modules/search";
import { urlAsync, urlSelectors } from "core/store/modules/url";
import { Dispatch, RootState } from "core/store/store.types";
import { DomainSource, Practice } from "core/types/practice.types";
import { ResultList } from "core/types/search.types";
import { Url } from "core/types/url.types";
import { User } from "core/types/user.types";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import PeriodDashboard from "./period-dashboard";

interface OwnProps {}
interface StateProps {
  user?: User;
  resultList?: ResultList[];
  practiceList?: Practice[];
  isSearchLoading: boolean;
  isPracticesLoading: boolean;
  urlList?: Url[];
}
interface DispatchProps {
  getPracticeList: () => void;
  getAdminPracticeList: () => void;
  getSearchList: () => void;
  getPersonalUrlList: () => void;
  getAllUrlList: () => void;
}

export type Props = OwnProps & StateProps & DispatchProps;

const Dashboard: FunctionComponent<Props> = ({
  user,
  resultList,
  isSearchLoading,
  practiceList,
  isPracticesLoading,
  urlList,
  getSearchList,
  getPracticeList,
  getAdminPracticeList,
  getPersonalUrlList,
  getAllUrlList,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { session: actAsSession } = useActAs();

  // Durante una delega il ruolo resta quello dell'operatore (/account non e' delegabile).
  const isOperator = isOperatorRole(user?.role);
  // Resoconto per periodo: prima agli operatori, ai clienti con la release del martedi'.
  const showPeriodDashboard = isOperator || DASHBOARD_PERIOD_FOR_ALL;

  const [orderedSearchList, setOrderedSearchList] = useState<ResultList[]>();
  const [orderedPracticeList, setOrderedPracticeList] = useState<Practice[]>();
  const [sourceList, setSourceList] = useState<DomainSource[]>([]);
  const [urlForGraph, setUrlForGraph] = useState<Record<string, number>>({});

  const getUrlsDomains = useCallback(() => {
    const risultatoMap = new Map<string, DomainSource>();

    if (urlList) {
      urlList.forEach((url) => {
        if (!risultatoMap.has(url.domain.domain)) {
          risultatoMap.set(url.domain.domain, {
            source: url.domain.domain,
            value: 1,
            impact: 0,
          });
        } else {
          const result = risultatoMap.get(url.domain.domain)!;
          result.value++;
        }
      });

      const resultList = Array.from(risultatoMap.values()).map((result) => ({
        ...result,
      }));

      const sumValues = resultList.reduce(
        (acc, result) => acc + result.value,
        0
      );

      resultList.forEach((result) => {
        result.impact = Math.round((result.value / sumValues) * 100);
      });

      return resultList;
    }
    return [];
  }, [urlList]);

  useEffect(() => {
    getSearchList();
  }, [getSearchList]);

  useEffect(() => {
    if (!user) return;
    // I ruoli sono minuscoli: il vecchio confronto con "ADMIN" non scattava mai e gli
    // operatori vedevano solo le proprie pratiche. In delega serve invece /legalPractices
    // (delegabile, scopata sul cliente): /admin/legalPractices partirebbe senza X-Act-As.
    if (isOperator && !actAsSession) {
      getAdminPracticeList();
    } else {
      getPracticeList();
    }
  }, [user, isOperator, actAsSession, getPracticeList, getAdminPracticeList]);

  useEffect(() => {
    if (practiceList) {
      setOrderedPracticeList(
        practiceList
          .sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
          .slice(0, 5)
      );
    }
  }, [practiceList]);

  useEffect(() => {
    setSourceList(getUrlsDomains());
  }, [getUrlsDomains]);

  useEffect(() => {
    if (resultList) {
      setOrderedSearchList(
        resultList
          .sort(
            (a, b) =>
              new Date(b.searchCreationTime).getTime() -
              new Date(a.searchCreationTime).getTime()
          )
          .slice(0, 7)
      );
    }
  }, [resultList]);

  useEffect(() => {
    // Gli URL servono solo alla dashboard vecchia (ciambella e tabella domini).
    if (!user || showPeriodDashboard) return;
    if (user?.role === "admin" || user?.role === "legals") {
      getAllUrlList();
    } else {
      getPersonalUrlList();
    }
  }, [user, showPeriodDashboard, getAllUrlList, getPersonalUrlList]);

  useEffect(() => {
    if (urlList) {
      setUrlForGraph(countUrlStatus(urlList));
    }
  }, [urlList]);

  function countUrlStatus(urls: Url[]): Record<string, number> {
    let onlineCount = 0;
    let pendingCount = 0;
    let removedCount = 0;

    for (const urlObj of urls) {
      const status = urlObj.status;
      if (status === "online") {
        onlineCount += 1;
      } else if (status === "pending") {
        pendingCount += 1;
      } else if (status === "removed") {
        removedCount += 1;
      }
    }

    const result: Record<string, number> = {
      online: onlineCount,
      pending: pendingCount,
      removed: removedCount,
    };

    return result;
  }

  const lastSearchesCard = (
    <Card sx={{ height: "100%" }}>
      <Grid
        container
        justifyContent={"space-between"}
        alignItems={"center"}
        spacing={6}
      >
        <Grid item>
          <Text
            fontSize={"16px"}
            fontWeight={"700"}
            paddingY={"15px"}
            paddingLeft={"8px"}
          >
            {t("dashboard.last_research")}
          </Text>
        </Grid>
        <Grid item>
          <IconButton onClick={() => navigate("/web-rep/searches")}>
            <OpenInNewRoundedIcon color="info" />
          </IconButton>
        </Grid>
      </Grid>
      <ResearchListTable
        searchList={orderedSearchList}
        isSearchLoading={isSearchLoading}
      />
    </Card>
  );

  const practicesCard = (
    <Card>
      <Grid
        container
        justifyContent={"space-between"}
        alignItems={"center"}
        spacing={6}
      >
        <Grid item>
          <Text
            fontSize={"16px"}
            fontWeight={"700"}
            paddingY={"15px"}
            paddingLeft={"8px"}
          >
            {t("practices.title")}
          </Text>
        </Grid>
        <Grid item>
          <IconButton onClick={() => navigate("/web-rep/practices")}>
            <OpenInNewRoundedIcon color="info" />
          </IconButton>
        </Grid>
      </Grid>
      <PracticeListTable
        practicesList={orderedPracticeList}
        isPracticeLoading={isPracticesLoading}
      />
    </Card>
  );

  if (showPeriodDashboard) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PeriodDashboard isOperator={isOperator} />
        </Grid>
        <Grid item xs={12}>
          {lastSearchesCard}
        </Grid>
        <Grid item xs={12}>
          {practicesCard}
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid container item spacing={3}>
        <Grid item xs={12} sm={8}>
          {lastSearchesCard}
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{height: '100%'}}>
            <Text
              fontSize={"16px"}
              fontWeight={"700"}
              paddingY={"15px"}
              paddingLeft={"8px"}
            >
              {t("dashboard.sources")}
            </Text>
            <Chart list={urlForGraph} />
            <DomainsTable
              isLoading={isPracticesLoading || sourceList.length === 0}
              list={sourceList}
            />
          </Card>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        {practicesCard}
      </Grid>
    </Grid>
  );
};

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  (state: RootState) => ({
    isSearchLoading: searchSelectors.isSearchLoading(state),
    user: authSelectors.getUser(state),
    resultList: searchSelectors.getResultList(state),
    practiceList: practiceSelectors.getPracticesList(state),
    isReportLoading: reportSelectors.isReportLoading(state),
    isPracticesLoading: practiceSelectors.isPracticeLoading(state),
    urlList: urlSelectors.getUrlList(state),
  }),
  (dispatch: Dispatch) => ({
    getSearchList: () => dispatch(searchAsync.getSearches()),
    getPracticeList: () => dispatch(practiceAsync.getPractices()),
    getAdminPracticeList: () => dispatch(practiceAsync.getAdminPractices()),
    getAllUrlList: () => dispatch(urlAsync.getAllUrls()),
    getPersonalUrlList: () => dispatch(urlAsync.getAllPersonalUrls()),
  })
)(Dashboard);
